import { convertUnix } from "./convert-unix";
import { getStartOfDay } from "./get-start-of-day";
import {
  shipmentQuantityKeys,
  filterDisplayAppointments,
} from "../components/Constants";
import { convertUnixDate } from "./convert-unix";
import { normalizeTempZone } from "./normalize-tempzone";

function countManifestedVrids(
  incomingIncomingShipments,
  incomingDockReceived,
  timeZone
) {
  // Group shipments by date
  const shipmentsByDate = incomingIncomingShipments.reduce((acc, shipment) => {
    // Only process Transship loadType
    if (
      !shipment.scheduledArrivals ||
      !shipment.scheduledArrivals.length ||
      shipment.loadType !== "Transship"
    )
      return acc;

    const date = convertUnixDate(shipment.scheduledArrivals[0], timeZone);
    if (!acc[date]) {
      acc[date] = {
        date,
        allVrids: new Set(),
        manifestedVrids: new Set(),
      };
    }

    // Get VRIDs from either vehicleRunId or shipmentId, excluding TSV and null
    const vrids = [];
    if (
      shipment.vehicleRunId &&
      shipment.vehicleRunId !== null &&
      !shipment.vehicleRunId.includes("TSV")
    ) {
      vrids.push(shipment.vehicleRunId);
    }
    if (Array.isArray(shipment.shipmentId)) {
      vrids.push(
        ...shipment.shipmentId.filter(
          (id) => id !== null && !id.includes("TSV")
        )
      );
    }

    // Add VRIDs to the day's collection
    vrids.forEach((vrid) => {
      if (vrid) {
        acc[date].allVrids.add(vrid);

        // Check if this is a manifested transshipment
        const isManifested =
          (incomingIncomingShipments || []).some(
            (s) => s.vehicleRunId === vrid && s.orderId?.startsWith("Trans")
          ) ||
          (incomingDockReceived || []).some(
            (s) => s.vehicleRunId === vrid && s.orderId?.startsWith("Trans")
          );

        if (isManifested) {
          acc[date].manifestedVrids.add(vrid);
        }
      }
    });

    return acc;
  }, {});

  // Also process dock received shipments
  (incomingDockReceived || []).forEach((shipment) => {
    if (shipment.loadType !== "Transship") return;

    const date = convertUnixDate(shipment.dockReceived, timeZone);
    if (!shipmentsByDate[date]) {
      shipmentsByDate[date] = {
        date,
        allVrids: new Set(),
        manifestedVrids: new Set(),
      };
    }

    // Add VRID from dock received shipment
    if (shipment.vehicleRunId && !shipment.vehicleRunId.includes("TSV")) {
      shipmentsByDate[date].allVrids.add(shipment.vehicleRunId);

      // If it's in dock received and is a transshipment, it's manifested
      if (shipment.orderId?.startsWith("Trans")) {
        shipmentsByDate[date].manifestedVrids.add(shipment.vehicleRunId);
      }
    }
  });

  // Convert and sort the grouped data by date
  return Object.values(shipmentsByDate)
    .map(({ date, allVrids, manifestedVrids }) => ({
      date,
      manifestedVridsCount: manifestedVrids.size,
      totalVridsCount: allVrids.size,
      vrids: Array.from(allVrids),
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

function aggregateShipments(
  shipments,
  dockReceived,
  scheduled,
  unscheduled,
  time_zone
) {
  // Get start of today and end of day+4 in the given timezone
  const todayStart = getStartOfDay(time_zone, 0);
  const futureEnd = getStartOfDay(time_zone, 5); // day+4 = start of day 5

  // Create map to store aggregated data
  const detailsMap = new Map(); // For temp zone details

  // Template for creating new row entries
  const createEmptyRow = (timeKey, dateKey, sourceType, tempZone) => ({
    id: `${dateKey}_${sourceType}_${tempZone}`,
    appointmentTime: timeKey,
    appointmentDate: dateKey,
    sourceType,
    tempZone,
    isSummary: false,
    expectedUnits: 0,
    shipmentCount: 0,
    scheduledUnits: 0,
    unscheduledUnits: 0,
    dockReceived: 0,
    // Add child quantity fields for proper getSummaryQuantity() support
    childExpectedUnits: 0,
    childScheduledUnits: 0,
    childUnscheduledUnits: 0,
    childDockReceived: 0,
    time_zone: time_zone,
  });

  // Helper function to get normalized temp zone from shipment
  const getNormalizedTempZone = (shipment) => {
    const rawTempZone = shipment.temp_zone || shipment.tempZone;
    return rawTempZone ? normalizeTempZone(rawTempZone) : null;
  };

  // Optimized aggregation function
  function aggregateShipmentData(shipmentArray, isScheduled) {
    if (!Array.isArray(shipmentArray) || shipmentArray.length === 0) return;

    for (const shipment of shipmentArray) {
    
      const units =
        shipment.expected_qty ||
        shipment.expectedUnits ||
        shipment.confirmedUnits ||
        0;
      
     
      let childUnits;
      if (isScheduled === "dockReceived") {
        
        const childReceived = shipment.childReceivedUnits;
        childUnits = (childReceived && childReceived >= units) ? childReceived : units;
      } else {
        
        const childExpected = shipment.childExpectedUnits || shipment.childConfirmedUnits;
        childUnits = (childExpected && childExpected >= units) ? childExpected : units;
      }
      
      // Determine sourceType - prioritize ib_flow (for SCIP forecasts), then loadType (for manifested shipments)
      let sourceType = shipment.loadType;
      if (shipment.ib_flow === 'TSI') {
        sourceType = 'Transship';
      } else if (shipment.ib_flow === 'DR') {
        sourceType = 'Vendor';
      } else if (!shipment.loadType) {
        // Fallback for manifested shipments without loadType
        sourceType = 'Vendor';
      }

      // For scheduled shipments, process all scheduledArrivals dates
      // Only process shipments from today onwards
      if (isScheduled === "forecasts" && shipment.expected_arrival_time) {
        const timeKey = convertUnixDate(
          parseInt(shipment.expected_arrival_time),
          time_zone
        );
        const shipmentDate = new Date(timeKey);
        const dateKey = shipmentDate.toDateString();

        // Normalize temp zone to remove EU suffixes (-24, -3) for proper aggregation
        const tempZone = getNormalizedTempZone(shipment);
        if (!tempZone) continue; // Skip if no temp zone info

        const detailKey = `${dateKey}_${sourceType}_${tempZone}`;

        // Get or create detail row
        let detail = detailsMap.get(detailKey);
        if (!detail) {
          detail = createEmptyRow(timeKey, dateKey, sourceType, tempZone);
          detailsMap.set(detailKey, detail);
        }

        // Update units for detail (both parent and child)
        detail.expectedUnits += units;
        detail.childExpectedUnits += childUnits;
      } else if (
        isScheduled !== "dockReceived" &&
        shipment.scheduledArrivals &&
        shipment.scheduledArrivals.length > 0
      ) {
        // Get the earliest arrival time for this shipment
        const earliestArrival = Math.min(...shipment.scheduledArrivals);
        // Skip shipments before today or after today+4
        if (earliestArrival >= futureEnd) {
          continue;
        }
        // Track processed dates for this shipment to avoid double counting
        const processedDates = new Set();

        for (const arrivalTime of shipment.scheduledArrivals) {
          const timeKey = convertUnixDate(arrivalTime, time_zone);
          const shipmentDate = new Date(timeKey);
          const dateKey = shipmentDate.toDateString();

          // Skip if we've already processed this date for this shipment
          if (processedDates.has(dateKey)) {
            continue;
          }
          processedDates.add(dateKey);

          // Normalize temp zone for manifested shipments to match forecast format
          const tempZone = getNormalizedTempZone(shipment);
          if (!tempZone) continue; // Skip if no temp zone info
          
          const detailKey = `${dateKey}_${sourceType}_${tempZone}`;

          // Get or create detail row
          let detail = detailsMap.get(detailKey);
          if (!detail) {
            detail = createEmptyRow(timeKey, dateKey, sourceType, tempZone);
            detailsMap.set(detailKey, detail);
          }
          
          if (isScheduled === "scheduled") {
            // Add both parent and child quantities
            detail.scheduledUnits += units;
            detail.childScheduledUnits += childUnits;
          } else if (isScheduled === "unscheduled") {
            // Add both parent and child quantities
            detail.unscheduledUnits += units;
            detail.childUnscheduledUnits += childUnits;
          }
        }
      } else if (
        shipment.dockReceived &&
        shipment.dockReceived >= todayStart &&
        shipment.dockReceived < futureEnd
      ) {
        const timeKey = convertUnixDate(shipment.dockReceived, time_zone);
        const shipmentDate = new Date(timeKey);
        const dateKey = shipmentDate.toDateString();
        
        // Normalize temp zone for dock received shipments to match forecast format
        const tempZone = getNormalizedTempZone(shipment);
        if (!tempZone) continue; // Skip if no temp zone info
        
        const detailKey = `${dateKey}_${sourceType}_${tempZone}`;

        // Get or create detail row
        let detail = detailsMap.get(detailKey);
        if (!detail) {
          detail = createEmptyRow(timeKey, dateKey, sourceType, tempZone);
          detailsMap.set(detailKey, detail);
        }
        
        if (isScheduled === "dockReceived") {
          // Add both parent and child quantities
          detail.dockReceived += units;
          detail.childDockReceived += childUnits;
        }
      }
    }
  }

  // Aggregate scheduled and unscheduled shipments
  try {
    if (scheduled) aggregateShipmentData(scheduled, "scheduled");
    if (unscheduled) aggregateShipmentData(unscheduled, "unscheduled");
    if (shipments) aggregateShipmentData(shipments, "forecasts");
    if (dockReceived) aggregateShipmentData(dockReceived, "dockReceived");
  } catch (error) {
    console.error("Error aggregating shipments:", error);
  }

  // Sort rows by date before returning
  const detailRows = Array.from(detailsMap.values()).sort(
    (a, b) => new Date(a.appointmentDate) - new Date(b.appointmentDate)
  );

  return { detailRows };
}

function parseShipmentsUpdated(
  gsfShipmentItemSummaries,
  ocrVendors = [],
  time_zone,
  scipData = [],
  tsiForecastData = [],
  regionId
) {
  const {
    vendorConfirmedUnits,
    vendorReceivedUnits,
    transConfirmedUnits,
    transReceivedUnits,
  } = shipmentQuantityKeys;
  let timeZoneIncluded = true;
  if (!time_zone) {
    time_zone = "UTC";
    timeZoneIncluded = false;
  }
  if (!gsfShipmentItemSummaries) {
    return { parsedIncomingShipments: [], parsedDockReceived: [] };
  }
  let incomingDockReceived = [];
  let incomingIncomingShipments = [];
  let incomingIncomingShipmentsLoadId = [];
  let incomingUnscheduledShipments = []; 
  let incomingUnscheduledShipmentsLoadId = [];
  let scipForecastsSummary = [];

  for (const gsfShipmentItemSummary of gsfShipmentItemSummaries) {
    // Flatten out object to an array for easier processing
    const associatedAppointments = gsfShipmentItemSummary.associatedAppointments
      ? Object.entries(
          JSON.parse(gsfShipmentItemSummary.associatedAppointments)
        ).map(([appointmentId, appointmentDetails]) => appointmentDetails)
      : [];
    // Sort appointments
    const appointmentDetail = associatedAppointments.sort((a, b) => {
      if (a.scheduledArrivalTime < b.scheduledArrivalTime) {
        return -1;
      }
      if (a.scheduledArrivalTime > b.scheduledArrivalTime) {
        return 1;
      }
      return 0;
    });

    // Sort dock receives to ensure correct order
    const dockReceiveHistory = gsfShipmentItemSummary.dockReceiveHistory
      ? JSON.parse(gsfShipmentItemSummary.dockReceiveHistory).sort((a, b) => {
          if (a.dockReceiveTime < b.dockReceiveTime) {
            return -1;
          }
          if (a.dockReceiveTime > b.dockReceiveTime) {
            return 1;
          }
          return 0;
        })
      : [];

    // May need to get more complex for this check, but we'll keep it like this for now
    if (
      !gsfShipmentItemSummary.firstDockReceiveTime && (
      (gsfShipmentItemSummary.firstAppointmentTime >=
        getStartOfDay(time_zone, -7) &&
      gsfShipmentItemSummary.firstAppointmentTime < getStartOfDay(time_zone, 7)) 
      || (gsfShipmentItemSummary.isvmi === 'Y' && gsfShipmentItemSummary.hasAsn === 'Y')
    )) {

      // Process LoadId level data first
      if (gsfShipmentItemSummary.poItemSummary !== null) {
        const loadType = gsfShipmentItemSummary.orderId.startsWith("PO_")
          ? "Vendor"
          : "Transship";
        // Initialize scheduledArrivals with proper fallbacks
        let scheduledArrivals1 = [];
        if (appointmentDetail && appointmentDetail.length > 0) {
          scheduledArrivals1 = filterDisplayAppointments(appointmentDetail)
            .map((appointment) => appointment.scheduledArrivalTime)
            .filter((time) => time != null);
        }
        // Default to firstAppointmentTime if no valid appointments and it exists
        if (
          scheduledArrivals1.length === 0 &&
          gsfShipmentItemSummary.firstAppointmentTime
        ) {
          scheduledArrivals1 = [gsfShipmentItemSummary.firstAppointmentTime];
        }

        // Calculate expectedUnitSource similar to tempZone shipments
        let expectedUnitSource = "UNKNOWN";
        if (
          regionId !== 1 &&
          loadType === "Vendor" &&
          gsfShipmentItemSummary.expectedQuantityScip
        ) {
          expectedUnitSource = gsfShipmentItemSummary.expectedQtySourceScip;
        } else if (gsfShipmentItemSummary.poItemSummary) {
          expectedUnitSource =
            loadType === "Vendor"
              ? gsfShipmentItemSummary.orderCondition === "CompletelyConfirmed"
                ? "PO Confirmed"
                : "PO Submitted"
              : "Manifest";
        }

        let incomingItemSummary = { UNKNOWN: 0 };

        if (
          regionId !== 1 &&
          loadType === "Vendor" &&
          gsfShipmentItemSummary.expectedQuantityScip
        ) {
          incomingItemSummary = JSON.parse(
            gsfShipmentItemSummary.expectedQuantityScip
          );
          expectedUnitSource = gsfShipmentItemSummary.expectedQtySourceScip;
        } else if (gsfShipmentItemSummary.poItemSummary) {
          if (
            JSON.parse(gsfShipmentItemSummary.poItemSummary)
              .summaryByTempZoneDetailed &&
            Object.keys(
              JSON.parse(gsfShipmentItemSummary.poItemSummary)
                .summaryByTempZoneDetailed
            ).length > 0
          ) {
            if (loadType === "Vendor") {
              // VMI POs use ASN data for expected units
              if (gsfShipmentItemSummary.isvmi === 'Y' && gsfShipmentItemSummary.hasAsn === 'Y') {
                incomingItemSummary = Object.entries(
                  JSON.parse(gsfShipmentItemSummary.poItemSummary)
                    .summaryByTempZoneDetailed
                )
                  .map(([tempZone, quantities]) => ({
                    [tempZone]: quantities.childAsnQuantity || 0,
                  }))
                  .reduce((temp, quantity) => {
                    return Object.assign(temp, quantity);
                  });
                expectedUnitSource = "ASN Data";
              } else {
                // Regular vendor POs use existing logic
                incomingItemSummary = Object.entries(
                  JSON.parse(gsfShipmentItemSummary.poItemSummary)
                    .summaryByTempZoneDetailed
                )
                  .map(([tempZone, quantities]) => ({
                    [tempZone]:
                      quantities.quantityConfirmed > 0
                        ? quantities.quantityConfirmed
                        : quantities.quantitySubmitted,
                  }))
                  .reduce((temp, quantity) => {
                    return Object.assign(temp, quantity);
                  });
                expectedUnitSource =
                  gsfShipmentItemSummary.orderCondition === "CompletelyConfirmed"
                    ? "PO Confirmed"
                    : "PO Submitted";
              }
            } else {
              // SCIP predicts TSI for the simple temp zones. No Protein or Produce
              incomingItemSummary = Object.entries(
                JSON.parse(gsfShipmentItemSummary.poItemSummary)
                  .summaryByTempZone
              )
                .map(([tempZone, quantities]) => ({
                  [tempZone]:
                    quantities[transConfirmedUnits] > 0
                      ? quantities[transConfirmedUnits]
                      : quantities.wmsQuantityReceived,
                }))
                .reduce((temp, quantity) => {
                  return Object.assign(temp, quantity);
                });
              expectedUnitSource = "Manifest";
            }
          }
        }

        // Initialize scheduledArrivals with proper fallbacks
        let scheduledArrivals = [];
        if (appointmentDetail && appointmentDetail.length > 0) {
          scheduledArrivals = filterDisplayAppointments(appointmentDetail)
            .map((appointment) => appointment.scheduledArrivalTime)
            .filter((time) => time != null);
        }
        // Default to firstAppointmentTime if no valid appointments and it exists
        if (
          scheduledArrivals.length === 0 &&
          gsfShipmentItemSummary.firstAppointmentTime
        ) {
          scheduledArrivals = [gsfShipmentItemSummary.firstAppointmentTime];
        }

        if (
          scheduledArrivals.length > 0 &&
          Math.max(...scheduledArrivals) > timeZoneIncluded
            ? getStartOfDay(time_zone, -1)
            : Date.now() - 24 * 60 * 60 * 1000
        ) {
          //  PO Temp Zone loop
          for (const [tempZone, tempZoneUnits] of Object.entries(
            incomingItemSummary
          )) {
            // Deep copy, so we can push to the appropriate array without affecting the other temp zones
            let tempZoneShipment = JSON.parse(
              JSON.stringify(gsfShipmentItemSummary)
            );
            // expected columns - need to make sure these exist otherwise csv and sort logic will not work
            // loadType, source, loadId, tempZone, status, scheduledArrivals, expectedArrivalTime, arrivalDatetime,
            // expectedUnits, expectedPallets, shipmentId, ocrVendor, scac
            tempZoneShipment.loadType = loadType;
            tempZoneShipment.source =
              loadType === "Vendor"
                ? tempZoneShipment.distributorId
                : tempZoneShipment.srcWarehouseId;
            tempZoneShipment.loadId = tempZoneShipment.orderId.substring(
              tempZoneShipment.orderId.indexOf("_") + 1
            );
            tempZoneShipment.tempZone = tempZone;
            tempZoneShipment.id = `${tempZoneShipment.loadId}-${tempZoneShipment.tempZone}`;
            tempZoneShipment.status =
              gsfShipmentItemSummary.freightArrivalStatus !== null
                ? [gsfShipmentItemSummary.freightArrivalStatus]
                : [undefined];

            tempZoneShipment.scheduledArrivals = scheduledArrivals;
             if (gsfShipmentItemSummary.isvmi === 'Y' 
              && gsfShipmentItemSummary.hasAsn === 'Y' 
              && gsfShipmentItemSummary.asnRecordFoundTime) {
                const midnightSnapshot = new Date(gsfShipmentItemSummary.asnRecordFoundTime);
                midnightSnapshot.setHours(0, 0, 0, 0);
                tempZoneShipment.scheduledArrivals = [midnightSnapshot.getTime()];
              }
            tempZoneShipment.scheduledArrivalsParsed =
              tempZoneShipment.scheduledArrivals.map((data) =>
                convertUnix(data, time_zone, "sv-SE")
              );

            tempZoneShipment.expectedArrivalTime =
              gsfShipmentItemSummary.freightEstimatedArrivalTime !== null
                ? [gsfShipmentItemSummary.freightEstimatedArrivalTime]
                : [undefined];
            tempZoneShipment.expectedArrivalTimeSource =
              tempZoneShipment.expectedArrivalTime[0] !== undefined
                ? ["10-4"]
                : [undefined];
            tempZoneShipment.expectedArrivalTimeParsed =
              tempZoneShipment.expectedArrivalTime
                ? tempZoneShipment.expectedArrivalTime.map((arrival) =>
                    arrival
                      ? convertUnix(arrival, time_zone, "sv-SE")
                      : undefined
                  )
                : [undefined];
            if (loadType === "Vendor") {
              tempZoneShipment.arrivalDatetime = [undefined];
              tempZoneShipment.arrivalDatetimeSource = [undefined];
              tempZoneShipment.arrivalDatetimeParsed = [undefined];
            } else {
              tempZoneShipment.arrivalDatetime =
                gsfShipmentItemSummary.freightActualArrivalTime !== null
                  ? [gsfShipmentItemSummary.freightActualArrivalTime]
                  : [undefined];
              tempZoneShipment.arrivalDatetimeSource =
                tempZoneShipment.arrivalDatetime[0] !== undefined
                  ? ["10-4"]
                  : [undefined];
              tempZoneShipment.arrivalDatetimeParsed =
                tempZoneShipment.arrivalDatetime
                  ? tempZoneShipment.arrivalDatetime.map((arrival) =>
                      arrival
                        ? convertUnix(arrival, time_zone, "sv-SE")
                        : undefined
                    )
                  : [undefined];
            }
            tempZoneShipment.expectedUnits = tempZoneUnits;
            // tempZoneShipment.expectedPallets = Math.ceil(
            //   (tempZoneUnits * 0.1) / 40
            // );
            tempZoneShipment.firstAppointmentSource =
              gsfShipmentItemSummary.firstAppointmentSource;
            tempZoneShipment.handoffWindowEndDateUTC =
              gsfShipmentItemSummary.handoffWindowEndDateUTC
                ? gsfShipmentItemSummary.handoffWindowEndDateUTC
                : null;
            tempZoneShipment.handoffWindowStartDateUTC =
              gsfShipmentItemSummary.handoffWindowStartDateUTC
                ? gsfShipmentItemSummary.handoffWindowStartDateUTC
                : null;
            tempZoneShipment.departureTime =
              gsfShipmentItemSummary.departureTime
                ? gsfShipmentItemSummary.departureTime
                : null;
            tempZoneShipment.estimatedArrivalTime =
              gsfShipmentItemSummary.estimatedArrivalTime
                ? gsfShipmentItemSummary.estimatedArrivalTime
                : null;

            // Get pallet count from manifest and SHREK tracking
            if (loadType === "Transship") {
              // Bug with missing vrid on freshly manifested TSI
              // if (!gsfShipmentItemSummary.vehicleRunId) {
              //   continue;
              // }
              const manifestSummary = gsfShipmentItemSummary.manifestSummary
                ? JSON.parse(gsfShipmentItemSummary.manifestSummary)
                : {};
              const palletSummary = manifestSummary.palletSummary
                ? manifestSummary.palletSummary
                : {};
              let expectedPallets = 0;

              for (const key of Object.keys(palletSummary)) {
                const palletDetails = palletSummary[key];
                if (palletDetails) {
                  // This may overstate our pallets if there's mixed pallets
                  // Swap to palletDetails.tempZone === tempZone and uncomment the else if below if we want to change
                  if (palletDetails.tempZone.includes(tempZone)) {
                    expectedPallets += 1;
                    // Let's only include this if we want to count split pallets as half
                    // } else if (palletDetails.tempZone.includes(tempZone)) {
                    //   expectedPallets += .5;
                    // }
                  }
                }
              }
              tempZoneShipment.expectedPallets = expectedPallets;

              let shrekData = tsiForecastData.filter(
                (tsiScip) =>
                  tsiScip.vehicleRunId === tempZoneShipment.vehicleRunId
              );
              if (shrekData.length > 0) {
                shrekData = shrekData[0];
              }

              tempZoneShipment.expectedArrivalTime =
                tempZoneShipment.vrEstimatedTimeOfArrival
                  ? [tempZoneShipment.vrEstimatedTimeOfArrival]
                  : shrekData.vrPlannedCheckinTime
                  ? [shrekData.vrPlannedCheckinTime]
                  : [undefined];
              tempZoneShipment.expectedArrivalTimeSource =
                tempZoneShipment.vrEstimatedTimeOfArrival
                  ? ["ETAPrediktion"]
                  : shrekData.vrPlannedCheckinTime
                  ? ["SHREK"]
                  : [undefined];
              tempZoneShipment.arrivalDatetime =
                shrekData.vrCheckinCompletionTime
                  ? [shrekData.vrCheckinCompletionTime]
                  : [undefined];
              tempZoneShipment.arrivalDatetimeSource =
                shrekData.vrCheckinCompletionTime ? ["SHREK"] : [undefined];
            }

            // TODO for vendor
            tempZoneShipment.shipmentId =
              loadType === "Vendor" ? [] : [tempZoneShipment.vehicleRunId];

            tempZoneShipment.ocrVendor =
              ocrVendors.filter(
                (ocrVendor) =>
                  ocrVendor.distributorId === tempZoneShipment.source
              ).length > 0
                ? "Y"
                : "N";

            tempZoneShipment.scac = appointmentDetail.map(
              (appointment) => appointment.carrierId
            );

            tempZoneShipment.timeZone = time_zone;
            tempZoneShipment.expectedUnitSource = expectedUnitSource;

            let tempZoneUnitsDetailed = JSON.parse(
              tempZoneShipment.poItemSummary
            ).summaryByTempZoneDetailed;
            tempZoneShipment.asrsConfirmedUnits =
              loadType === "Vendor"
                ? tempZoneUnitsDetailed[tempZone]?.asrsQuantityConfirmed ?? 0
                : tempZoneUnitsDetailed[tempZone]?.asrsMainifestQuantity ?? 0;
            tempZoneShipment.confirmedUnits =
              loadType === "Vendor"
                ? tempZoneUnitsDetailed[tempZone]?.quantityConfirmed ?? 0
                : tempZoneUnitsDetailed[tempZone]?.manifestQuantity ??
                  tempZoneUnitsDetailed[tempZone]?.wmsQuantityReceived;
            // Fix NaN issue by adding safety check for division by zero
            tempZoneShipment.expectedAsrsUnits = tempZoneShipment.confirmedUnits > 0 
              ? Math.round(
                  (tempZoneShipment.asrsConfirmedUnits /
                    tempZoneShipment.confirmedUnits) *
                    tempZoneShipment.expectedUnits
                )
              : 0;
            tempZoneShipment.expectedAsrsUnitsPercent = tempZoneShipment.confirmedUnits > 0
              ? Math.round(
                  (tempZoneShipment.asrsConfirmedUnits /
                    tempZoneShipment.confirmedUnits) *
                    100 *
                    100
                ) / 100
              : 0;

            // Add child quantities for incoming shipments
            const tempZoneData = tempZoneUnitsDetailed[tempZone] || {};
            if (gsfShipmentItemSummary.isvmi === 'Y' && gsfShipmentItemSummary.hasAsn === 'Y') {
                tempZoneShipment.childExpectedUnits = tempZoneShipment.expectedUnits;
            } else {
                // Mirror the parent logic: use confirmed if > 0, otherwise use submitted, fallback to parent
                tempZoneShipment.childExpectedUnits = 
                    tempZoneData.childQuantityConfirmed > 0 
                        ? tempZoneData.childQuantityConfirmed 
                        : tempZoneData.childQuantitySubmitted || tempZoneShipment.expectedUnits;
            }
            tempZoneShipment.childExpectedAsrsUnits = tempZoneData.childAsrsQuantityConfirmed || tempZoneShipment.expectedAsrsUnits;


            tempZoneShipment.criticalAsinEvents = tempZoneShipment.eventSummary
              ? Object.values(JSON.parse(tempZoneShipment.eventSummary))
                  // Following MMv1 logic. Show if event start is at most 2 weeks out and hasn't already ended
                  .filter(
                    (criticalEvent) =>
                      (criticalEvent.startDate >=
                        Date.now() - 14 * 24 * 60 * 60 * 1000 &&
                        criticalEvent.endDate >= Date.now()) ||
                      (criticalEvent.startDate <= Date.now() &&
                        criticalEvent.endDate >= Date.now())
                  )
              : [];

            // Sometimes SCIP predicts 0. Let's just not show that
            if (tempZoneShipment.expectedUnits > 0) {
            if (tempZoneShipment.firstAppointmentSource === "APPOINTMENT") {
              // This is for debugging
              tempZoneShipment.logicSource = "Incoming";
              incomingIncomingShipments.push(tempZoneShipment);
            } else if (gsfShipmentItemSummary.isvmi === 'Y' && gsfShipmentItemSummary.hasAsn === 'Y') {
              incomingIncomingShipments.push(tempZoneShipment);
            } else if (tempZoneShipment.loadType === 'Vendor' && tempZoneShipment.orderCondition === 'CompletelyConfirmed') {
              // Store temp zone level details - only show confirmed Vendor POs in pending table
              incomingUnscheduledShipments.push(tempZoneShipment);
            } else if (tempZoneShipment.loadType === 'Transship') {
              // Show all Transship in pending table (no confirmation needed)
              incomingUnscheduledShipments.push(tempZoneShipment);
            }
            // If Vendor PO and not confirmed, don't show in either table
            }
          }
        }
      }
    }

    if (gsfShipmentItemSummary.firstDockReceiveTime) {
      // Dock Received
      //  Temp Zone loop
      const poItemSummary = gsfShipmentItemSummary.poItemSummary
        ? JSON.parse(gsfShipmentItemSummary.poItemSummary)
        : {};
      const summaryByTempZoneDetailed = poItemSummary.summaryByTempZoneDetailed
        ? poItemSummary.summaryByTempZoneDetailed
        : {};

      for (const [tempZone, tempZoneUnits] of Object.entries(
        summaryByTempZoneDetailed
      )) {
        // Deep copy, so we can push to the appropriate array without affecting the other temp zones
        let tempZoneShipment = JSON.parse(
          JSON.stringify(gsfShipmentItemSummary)
        );
        // Table Columns:
        // loadType, source, loadId, tempZone, actualArrival, dockReceived, lastDockReceiveBy, firstReceiveTime
        // confirmedUnits, receivedUnits, nyrnys, progress, dwellDays, timeToSLA,
        const loadType = tempZoneShipment.orderId.startsWith("PO_")
          ? "Vendor"
          : "Transship";
        tempZoneShipment.loadType = loadType;
        tempZoneShipment.source =
          loadType === "Vendor"
            ? tempZoneShipment.distributorId
            : tempZoneShipment.srcWarehouseId;
        tempZoneShipment.loadId = tempZoneShipment.orderId.substring(
          tempZoneShipment.orderId.indexOf("_") + 1
        );
        tempZoneShipment.tempZone = tempZone;
        tempZoneShipment.actualArrival =
          loadType === "Vendor"
            ? null
            : tempZoneShipment.vrCheckinCompletionTime;
        // This is for the csv
        tempZoneShipment.actualArrivalParsed = convertUnix(
          tempZoneShipment.actualArrival,
          time_zone,
          "sv-SE"
        );
        tempZoneShipment.dockReceived = tempZoneShipment.firstDockReceiveTime;
        // This is for the csv
        tempZoneShipment.dockReceivedParsed = convertUnix(
          tempZoneShipment.dockReceived,
          time_zone,
          "sv-SE"
        );
        tempZoneShipment.lastDockReceiveBy =
          tempZoneShipment.firstDockReceiveBy;
        // This is for the csv
        tempZoneShipment.firstReceiveTimeParsed = convertUnix(
          tempZoneShipment.firstReceiveTime,
          time_zone,
          "sv-SE"
        );
        // TODO: Let's simplify this after a couple weeks when manifests should be in all shipments
        tempZoneShipment.confirmedUnits =
          loadType === "Vendor"
            ? tempZoneUnits[vendorConfirmedUnits]
            : tempZoneUnits[transConfirmedUnits]
            ? tempZoneUnits[transConfirmedUnits]
            : tempZoneUnits.wmsQuantityReceived;
        tempZoneShipment.receivedUnits =
          loadType === "Vendor"
            ? tempZoneUnits[vendorReceivedUnits]
            : tempZoneUnits[transReceivedUnits];
        tempZoneShipment.nyrnys =
          tempZoneShipment.confirmedUnits - tempZoneShipment.receivedUnits;
        if (tempZoneShipment.nyrnys < 0) {
          tempZoneShipment.nyrnys = 0;
        }
        tempZoneShipment.progress =
          Math.round(
            (tempZoneShipment.receivedUnits / tempZoneShipment.confirmedUnits) *
              10000
          ) / 100;
        tempZoneShipment.dwellDays =
          Math.floor(
            ((Date.now() - tempZoneShipment.dockReceived) /
              (24 * 60 * 60 * 1000)) *
              100
          ) / 100;
        tempZoneShipment.sla =
          loadType === "Vendor"
            ? tempZoneUnits.vendorReceiveSlaFcs
            : tempZoneUnits.tshipSla;
        tempZoneShipment.asinCube = tempZoneUnits.asinCube;

        tempZoneShipment.asrsConfirmedUnits =
          loadType === "Vendor"
            ? tempZoneUnits.asrsQuantityConfirmed
              ? tempZoneUnits.asrsQuantityConfirmed
              : 0
            : tempZoneUnits.asrsMainifestQuantity
            ? tempZoneUnits.asrsMainifestQuantity
            : 0;
        tempZoneShipment.asrsReceivedUnits =
          loadType === "Vendor"
            ? tempZoneUnits.asrsQuantityReceived
              ? tempZoneUnits.asrsQuantityReceived
              : 0
            : tempZoneUnits.asrsQuantityReceivedWmsStow
            ? tempZoneUnits.asrsQuantityReceivedWmsStow
            : 0;
        tempZoneShipment.asrsConfirmedUnitsPercent =
          Math.round(
            (tempZoneShipment.asrsConfirmedUnits /
              tempZoneShipment.confirmedUnits) *
              100 *
              100
          ) / 100;

        // Add child quantities for dock received
        tempZoneShipment.childConfirmedUnits = tempZoneUnits.childQuantityConfirmed || tempZoneShipment.confirmedUnits;
        tempZoneShipment.childAsrsConfirmedUnits = tempZoneUnits.childAsrsQuantityConfirmed || tempZoneShipment.asrsConfirmedUnits;
        tempZoneShipment.childReceivedUnits = tempZoneUnits.childQuantityReceived || tempZoneShipment.receivedUnits;
        tempZoneShipment.childAsrsReceivedUnits = tempZoneUnits.childAsrsQuantityReceived || tempZoneShipment.asrsReceivedUnits;
        
        // Use pre-aggregated NYR/NYS from DDB if available, otherwise calculate
        // Vendors use childNyrQuantityPO (NYR), Transships use nysQuantityWMS (NYS - Not Yet Stowed)
        if (loadType === "Vendor") {
          tempZoneShipment.childNyrnys = tempZoneUnits.childNyrQuantityPO ?? 
            Math.max(0, tempZoneShipment.childConfirmedUnits - tempZoneShipment.childReceivedUnits);
        } else {
          // Transship - use NYS (Not Yet Stowed) as per Amplify version
          // Formula from wiki: nysQuantityWMS = manifestQuantity - wmsQuantityReceivedStow
          tempZoneShipment.childNyrnys = tempZoneUnits.nysQuantityWMS ?? 
            Math.max(0, tempZoneShipment.childConfirmedUnits - (tempZoneUnits.wmsQuantityReceivedStow ?? 0));
        }

        // Automatic Suppression if 97% received and 24 hours past dock receive
        // tempZoneShipment.suppression = (tempZoneShipment.receivedUnits / tempZoneShipment.confirmedUnits >= 0.97) && (Date.now()>=tempZoneShipment.dockReceived+24*60*60*1000);
        if (tempZoneShipment.suppressionHistory) {
          const suppressionHistory = tempZoneShipment.suppressionHistory
            ? JSON.parse(tempZoneShipment.suppressionHistory)
            : [];
          let tempZoneSuppressions = [];
          try {
            tempZoneSuppressions = suppressionHistory
                .filter((suppression) => suppression.tempZone === tempZone)
                .sort((a, b) => b.suppressionTime - a.suppressionTime);
          } catch (e) {
            console.log(e);
            console.log(suppressionHistory);
          }
          if (tempZoneSuppressions.length > 0) {
            const tempZoneSuppression = tempZoneSuppressions[0];
            tempZoneShipment.suppression = tempZoneSuppression.suppressed
              ? tempZoneSuppression.suppressed
              : false;
          } else {
            tempZoneShipment.suppression = false;
          }
        } else {
          tempZoneShipment.suppression =
            tempZoneShipment.receivedUnits / tempZoneShipment.confirmedUnits >=
              0.97 &&
            Date.now() >= tempZoneShipment.dockReceived + 24 * 60 * 60 * 1000;
        }
        tempZoneShipment.timeZone = time_zone;
        tempZoneShipment.vehicleRunId = gsfShipmentItemSummary.vehicleRunId;
        // Pallet Review Data
        if (loadType === "Transship") {
          const manifestSummary = gsfShipmentItemSummary.manifestSummary
            ? JSON.parse(gsfShipmentItemSummary.manifestSummary)
            : {};
          const palletSummary = manifestSummary.palletSummary
            ? manifestSummary.palletSummary
            : {};
          const receivedPallets = gsfShipmentItemSummary.palletIds
            ? JSON.parse(gsfShipmentItemSummary.palletIds)
            : [];
          for (const palletId of receivedPallets) {
            const pallet = palletSummary[palletId]
              ? palletSummary[palletId]
              : {};
            pallet.received = true;
            palletSummary[palletId] = pallet;
          }
          tempZoneShipment.palletSummary = Object.entries(palletSummary).map(
            ([palletId, palletData]) => {
              palletData.palletId = palletId;
              return palletData;
            }
          );
        }

        tempZoneShipment.criticalAsinEvents = tempZoneShipment.eventSummary
          ? Object.values(JSON.parse(tempZoneShipment.eventSummary))
              // Following MMv1 logic. Show if event start is at most 2 weeks out and hasn't already ended
              .filter(
                (criticalEvent) =>
                  (criticalEvent.startDate >=
                    Date.now() - 14 * 24 * 60 * 60 * 1000 &&
                    criticalEvent.endDate >= Date.now()) ||
                  (criticalEvent.startDate <= Date.now() &&
                    criticalEvent.endDate >= Date.now())
              )
          : [];

        // Corner case where we have 0 confirmed units but the units were ordered
        if (loadType === "Vendor" && tempZoneShipment.confirmedUnits <= 0) {
        } else {
          tempZoneShipment.timeZone = time_zone;

          // This is for debugging
          tempZoneShipment.logicSource = "Dock Received";
          incomingDockReceived.push(tempZoneShipment);
        }
      }

      // NYR Calculations need just the three basic temp zones
      const summaryByTempZone = poItemSummary.summaryByTempZone
        ? poItemSummary.summaryByTempZone
        : {};
      for (const [tempZone, tempZoneUnits] of Object.entries(
        summaryByTempZone
      )) {
        // Deep copy, so we can push to the appropriate array without affecting the other temp zones
        let tempZoneShipment = JSON.parse(
          JSON.stringify(gsfShipmentItemSummary)
        );
        tempZoneShipment.tempZone = tempZone;
        tempZoneShipment.asinCube = tempZoneUnits.asinCube;
        const loadType = tempZoneShipment.orderId.startsWith("PO_")
          ? "Vendor"
          : "Transship";
        tempZoneShipment.loadType = loadType;
        tempZoneShipment.sla =
          loadType === "Vendor"
            ? tempZoneUnits.vendorReceiveSlaFcs
            : tempZoneUnits.tshipSla;
        tempZoneShipment.source =
          loadType === "Vendor"
            ? tempZoneShipment.distributorId
            : tempZoneShipment.srcWarehouseId;
        tempZoneShipment.loadId = tempZoneShipment.orderId.substring(
          tempZoneShipment.orderId.indexOf("_") + 1
        );
        // TODO: Let's simplify this after a couple weeks when manifests should be in all shipments
        tempZoneShipment.confirmedUnits =
          loadType === "Vendor"
            ? tempZoneUnits[vendorConfirmedUnits]
            : tempZoneUnits[transConfirmedUnits]
            ? tempZoneUnits[transConfirmedUnits]
            : tempZoneUnits.wmsQuantityReceived;
        tempZoneShipment.receivedUnits =
          loadType === "Vendor"
            ? tempZoneUnits[vendorReceivedUnits]
            : tempZoneUnits[transReceivedUnits];
        tempZoneShipment.actualArrival =
          loadType === "Vendor"
            ? null
            : appointmentDetail[0] !== undefined
              ? appointmentDetail[0].arrivalTime
              : null;
        tempZoneShipment.dockReceived =
          dockReceiveHistory.length > 0
            ? dockReceiveHistory[dockReceiveHistory.length - 1].dockReceiveTime
            : undefined;
        // Automatic Suppression if 100% received
        tempZoneShipment.suppression =
          tempZoneShipment.receivedUnits / tempZoneShipment.confirmedUnits >= 1
            ? true
            : tempZoneShipment.suppression
            ? tempZoneShipment.suppression
            : false;

        tempZoneShipment.criticalAsinEvents = tempZoneShipment.eventSummary
          ? Object.values(JSON.parse(tempZoneShipment.eventSummary))
              // Following MMv1 logic. Show if event start is at most 2 weeks out and hasn't already ended
              .filter(
                (criticalEvent) =>
                  (criticalEvent.startDate >=
                    Date.now() - 14 * 24 * 60 * 60 * 1000 &&
                    criticalEvent.endDate >= Date.now()) ||
                  (criticalEvent.startDate <= Date.now() &&
                    criticalEvent.endDate >= Date.now())
              )
          : [];
        tempZoneShipment.timeZone = time_zone;

        // This is for debugging
        tempZoneShipment.logicSource = "Dock Received";
      }
    }
  }

  const tsiForecasts = scipData.filter(
    (scipItem) =>
      scipItem.ib_flow === "TSI" &&
      scipItem.source_ind === "DOCalculationService"
  );

  const tsiForecastsSCIP = scipData.filter(
    (scipItem) => scipItem.ib_flow === "TSI"
  );

  const manifestedIncomingTSIs = incomingIncomingShipments.filter(
    (incomingShipment) => incomingShipment.loadType === "Transship"
  );
  const dockReceivedTSIs = incomingDockReceived.filter(
    (incomingDockReceived) => incomingDockReceived.loadType === "Transship"
  );

  /**
   * Helper function to get SHREK data for a specific VRID
   * @param {string} vrid - Vehicle Run ID
   * @param {Array} tsiForecastData - Array of TSI forecast data from SHREK
   * @returns {Object|null} - SHREK data for the VRID or null
   */
  function getShrekDataForVrid(vrid, tsiForecastData) {
    return tsiForecastData.find((data) => data.vehicleRunId === vrid) || null;
  }

  /**
   * Determines if a specific VRID should be dropped for this spoke based on SHREK departure data.
   * Per ticket R2L-FORGE-16: Drop VRID if manifest qty is NULL/0 for THIS spoke AND either:
   * 1) Departed >1 hour ago with no manifest
   * 2) Vehicle execution status = COMPLETED with no manifest
   * 
   * @param {string} vrid - Specific Vehicle Run ID
   * @param {Object} shrekData - SHREK data for this VRID (contains departure info)
   * @param {string} sourceInd - source_ind from forecast (e.g., 'DOCalculationService')
   * @param {string} warehouseId - Destination spoke/warehouse ID
   * @param {string} tempZone - Temperature zone at spoke
   * @param {string} originFc - Source warehouse/HUB
   * @param {number} expectedQty - DOCM forecast quantity
   * @param {number} expectedArrivalTime - Expected arrival timestamp
   * @returns {{ drop: boolean, rule: number|null }} - Whether to drop and which rule triggered it
   */
  function shouldDropVrid(vrid, shrekData, sourceInd, warehouseId, tempZone, originFc, expectedQty, expectedArrivalTime) {
    if (sourceInd !== 'DOCalculationService') {
      return { drop: false, rule: null };
    }

    if (!shrekData) {
      return { drop: false, rule: null };
    }

    function formatExpectedArrival(time) {
      if (time && !isNaN(time)) {
        const date = new Date(time);
        return !isNaN(date.getTime()) ? date.toISOString() : 'N/A';
      }
      return 'N/A';
    }

    const now = Date.now();
    const oneHourMs = 60 * 60 * 1000;

    // Rule 1: Departed from source >1 hour ago and still no manifest
    if (shrekData.vrActualCheckoutTimeAtSrc && shrekData.vrStopStatusAtSource?.toUpperCase() === 'DEPARTED') {
      const timeSinceDeparture = now - shrekData.vrActualCheckoutTimeAtSrc;
      if (timeSinceDeparture > oneHourMs) {
        const expectedArrivalStr = formatExpectedArrival(expectedArrivalTime);
        const departedAtStr = new Date(shrekData.vrActualCheckoutTimeAtSrc).toISOString();
        console.log(
          `[R2L-FORGE-16] Rule: 1 | Dropping VRID ${vrid} | Spoke: ${warehouseId} | TempZone: ${tempZone} | ` +
          `Source: ${originFc || shrekData.srcWarehouseId} | ExpectedArrival: ${expectedArrivalStr} | ` +
          `ExpectedQty: ${expectedQty} | Departed: ${departedAtStr} (${Math.round(timeSinceDeparture / oneHourMs)}h ago)`
        );
        return { drop: true, rule: 1 };
      }
    }

    // Rule 2: Vehicle execution is COMPLETED and no manifest
    if (shrekData.vrVehicleExecutionStatus?.toUpperCase() === 'COMPLETED') {
      const expectedArrivalStr = formatExpectedArrival(expectedArrivalTime);
      console.log(
        `[R2L-FORGE-16] Rule: 2 | Dropping VRID ${vrid} | Spoke: ${warehouseId} | TempZone: ${tempZone} | ` +
        `Source: ${originFc || shrekData.srcWarehouseId} | ExpectedArrival: ${expectedArrivalStr} | ` +
        `ExpectedQty: ${expectedQty} | Status: COMPLETED`
      );
      return { drop: true, rule: 2 };
    }

    return { drop: false, rule: null };
  }

  for (const tsiForecast of tsiForecasts) {
    const allVrids = [
      ...new Set(tsiForecast.load_id.replaceAll(" ", "").split(",")),
    ];
    if (allVrids.length === 0) {
      continue;
    }

    const tempZone = tsiForecast.temp_zone;
    // Normalize temp zone to handle EU format variations (AMBIENT-24 → AMBIENT)
    const normalizedTempZone = normalizeTempZone(tempZone);
    const manifestedIncomingVrids = manifestedIncomingTSIs.filter(
      (manifestedIncomingTSI) =>
        allVrids.includes(manifestedIncomingTSI.vehicleRunId) &&
        normalizeTempZone(manifestedIncomingTSI.tempZone) === normalizedTempZone
    );
    const dockReceivedVrids = dockReceivedTSIs.filter(
      (dockReceivedShipment) =>
        allVrids.includes(dockReceivedShipment.vehicleRunId) &&
        normalizeTempZone(dockReceivedShipment.tempZone) === normalizedTempZone
    );

    const dropDetails = [];
    const remainingVrids = allVrids.filter((vrid) => {
      const hasAnyManifest =
        manifestedIncomingTSIs.some((m) => m.vehicleRunId === vrid) || dockReceivedTSIs.some((d) => d.vehicleRunId === vrid);
      if (hasAnyManifest) {
        return false;
      }

      const shrekData = getShrekDataForVrid(vrid, tsiForecastData);
      const result = shouldDropVrid(vrid, shrekData, tsiForecast.source_ind, tsiForecast.warehouse_id, normalizedTempZone, tsiForecast.origin_fc, tsiForecast.expected_qty, tsiForecast.expected_arrival_time);
      if (result.drop && result.rule) {
        dropDetails.push({ vrid, rule: result.rule });
      }
      return !result.drop;
    });


    // This means all vrids have a manifest or should be dropped
    if (remainingVrids.length <= 0) {
      continue;
    }

    let tempZoneTSIShipment = {};
    tempZoneTSIShipment.tempZone = normalizedTempZone;
    tempZoneTSIShipment.loadType = "Transship";
    tempZoneTSIShipment.loadId = "Not Yet Manifested";
    tempZoneTSIShipment.source = tsiForecast.origin_fc;

    // Calculate total manifest quantity across all VRIDs for this spoke
    const incomingManifestQuantity = manifestedIncomingVrids.reduce(
      (sum, filteredIncomingShipments) =>
        sum + filteredIncomingShipments.confirmedUnits,
      0
    );
    const dockReceivedManifestQuantity = dockReceivedVrids.reduce(
      (sum, filteredIncomingShipments) =>
        sum + filteredIncomingShipments.confirmedUnits,
      0
    );
    const totalManifestQuantity =
      incomingManifestQuantity + dockReceivedManifestQuantity;

    const expectedQuantity = Math.max(
      tsiForecast.expected_qty - totalManifestQuantity,
      0
    );

    tempZoneTSIShipment.expectedUnits = expectedQuantity;
    tempZoneTSIShipment.totalManifestQuantity = totalManifestQuantity;

    if (tempZoneTSIShipment.loadType === "Transship") {

      if (tsiForecast.source_ind === "DOCalculationService") {
        tempZoneTSIShipment.expectedUnitSource = `${
          tsiForecast.source_ind
        } Crossdock: ${tsiForecast.expected_qty_xd.toLocaleString()}, DO Calc: ${tsiForecast.expected_qty_doc.toLocaleString()}, MTR: ${tsiForecast.expected_qty_mtr.toLocaleString()}, Rolled Freight: ${tsiForecast.expected_qty_rf.toLocaleString()}`;
        tempZoneTSIShipment.stopSequence = tsiForecast.stop_sequence;
      } else {
        tempZoneTSIShipment.expectedUnitSource = `${tsiForecast.source_ind}`;
      }
      tempZoneTSIShipment.scheduledArrivals = [
        tsiForecast.expected_arrival_time,
      ];
      if (tsiForecast.source_ind === "DOCalculationService") {
        tempZoneTSIShipment.firstAppointmentSource = "ATS";
        tempZoneTSIShipment.stopSequence = tsiForecast.stop_sequence;
      } else {
        tempZoneTSIShipment.firstAppointmentSource = "SCIP";
      }
      tempZoneTSIShipment.scheduledArrivalsParsed =
        tempZoneTSIShipment.scheduledArrivals.map((data) =>
          convertUnix(data, time_zone, "sv-SE")
        );
      tempZoneTSIShipment.expectedArrivalTime = tsiForecast.expectedArrivalTime
        ? tsiForecast.expectedArrivalTime
        : [undefined];
      tempZoneTSIShipment.expectedArrivalTimeSource =
        tsiForecast.expectedArrivalTimeSource
          ? tsiForecast.expectedArrivalTimeSource
          : [undefined];
      tempZoneTSIShipment.expectedArrivalTimeParsed =
        tempZoneTSIShipment.expectedArrivalTime
          ? tempZoneTSIShipment.expectedArrivalTime.map((arrival) =>
              arrival ? convertUnix(arrival, time_zone, "sv-SE") : undefined
            )
          : [undefined];
      tempZoneTSIShipment.arrivalDatetime = tsiForecast.arrivalDatetime
        ? tsiForecast.arrivalDatetime
        : [undefined];
      tempZoneTSIShipment.arrivalDatetimeSource =
        tsiForecast.arrivalDatetimeSource
          ? tsiForecast.arrivalDatetimeSource
          : [undefined];
      tempZoneTSIShipment.arrivalDatetimeParsed =
        tempZoneTSIShipment.arrivalDatetime
          ? tempZoneTSIShipment.arrivalDatetime.map((arrival) =>
              arrival ? convertUnix(arrival, time_zone, "sv-SE") : undefined
            )
          : [undefined];

      tempZoneTSIShipment.shipmentId = remainingVrids;
      tempZoneTSIShipment.scac = "";
      tempZoneTSIShipment.ocrVendor = "N";
      tempZoneTSIShipment.timeZone = time_zone;
      tempZoneTSIShipment.transferId = tsiForecast.transferId;

      if (tempZoneTSIShipment.expectedUnits > 0) {
        // This is for debugging
        tempZoneTSIShipment.logicSource = "TSI Forecasts";
        incomingIncomingShipments.push(tempZoneTSIShipment);
      }
    }
  }

  for (const tsiScip of tsiForecastsSCIP) {
    const allVrids = [
      ...new Set(tsiScip.load_id.replaceAll(" ", "").split(",")),
    ];
    if (allVrids.length === 0) {
      continue;
    }

    const tempZone = tsiScip.temp_zone;
    const manifestedIncomingVrids = manifestedIncomingTSIs.filter(
      (manifestedIncomingTSI) =>
        allVrids.includes(manifestedIncomingTSI.vehicleRunId) &&
        manifestedIncomingTSI.tempZone === tempZone
    );
    const dockReceivedVrids = dockReceivedTSIs.filter(
      (dockReceivedShipment) =>
        allVrids.includes(dockReceivedShipment.vehicleRunId) &&
        dockReceivedShipment.tempZone === tempZone
    );

    // Filter VRIDs at per-VRID, per-spoke level (no global filter)
    const dropDetailsScip = [];
    const remainingVrids = allVrids.filter((vrid) => {
      // Calculate manifest quantity for THIS VRID at THIS spoke
      const vridIncomingQty = manifestedIncomingVrids
        .filter((m) => m.vehicleRunId === vrid)
        .reduce((sum, m) => sum + m.confirmedUnits, 0);

      const vridDockQty = dockReceivedVrids
        .filter((d) => d.vehicleRunId === vrid)
        .reduce((sum, d) => sum + d.confirmedUnits, 0);

      const vridTotalManifestQty = vridIncomingQty + vridDockQty;

      // If VRID has manifest for THIS spoke, exclude it
      if (vridTotalManifestQty > 0) {
        return false;
      }

      // No manifest for THIS spoke - check if should drop based on SHREK data
      const shrekData = getShrekDataForVrid(vrid, tsiForecastData);
      const result = shouldDropVrid(vrid, vridTotalManifestQty, shrekData, tsiScip.source_ind, tsiScip.warehouse_id, tsiScip.temp_zone, tsiScip.origin_fc, tsiScip.expected_qty, tsiScip.expected_arrival_time);
      if (result.drop && result.rule) {
        dropDetailsScip.push({ vrid, rule: result.rule });
      }
      return !result.drop;
    });

    // This means all vrids have a manifest or should be dropped
    if (remainingVrids.length <= 0) {
      continue;
    }

    let tempZoneTSIShipment = {};
    tempZoneTSIShipment.tempZone = tempZone;
    tempZoneTSIShipment.loadType = "Transship";
    tempZoneTSIShipment.loadId = "Not Yet Manifested";
    tempZoneTSIShipment.source = tsiScip.origin_fc;

    // Calculate total manifest quantity across all VRIDs for this spoke
    const incomingManifestQuantity = manifestedIncomingVrids.reduce(
      (sum, filteredIncomingShipments) =>
        sum + filteredIncomingShipments.confirmedUnits,
      0
    );
    const dockReceivedManifestQuantity = dockReceivedVrids.reduce(
      (sum, filteredIncomingShipments) =>
        sum + filteredIncomingShipments.confirmedUnits,
      0
    );
    const totalManifestQuantity =
      incomingManifestQuantity + dockReceivedManifestQuantity;

    const expectedQuantity = Math.max(
      tsiScip.expected_qty - totalManifestQuantity,
      0
    );

    tempZoneTSIShipment.expectedUnits = expectedQuantity;
    tempZoneTSIShipment.totalManifestQuantity = totalManifestQuantity;

    if (tempZoneTSIShipment.loadType === "Transship") {

      if (tsiScip.source_ind === "DOCalculationService") {
        tempZoneTSIShipment.expectedUnitSource = `${
          tsiScip.source_ind
        } Crossdock: ${tsiScip.expected_qty_xd.toLocaleString()}, DO Calc: ${tsiScip.expected_qty_doc.toLocaleString()}, MTR: ${tsiScip.expected_qty_mtr.toLocaleString()}, Rolled Freight: ${tsiScip.expected_qty_rf.toLocaleString()}`;
        tempZoneTSIShipment.stopSequence = tsiScip.stop_sequence;
      } else {
        tempZoneTSIShipment.expectedUnitSource = `${tsiScip.source_ind}`;
      }
      tempZoneTSIShipment.scheduledArrivals = [tsiScip.expected_arrival_time];
      if (tsiScip.source_ind === "DOCalculationService") {
        tempZoneTSIShipment.firstAppointmentSource = "ATS";
        tempZoneTSIShipment.stopSequence = tsiScip.stop_sequence;
      } else {
        tempZoneTSIShipment.firstAppointmentSource = "SCIP";
      }
      tempZoneTSIShipment.scheduledArrivalsParsed =
        tempZoneTSIShipment.scheduledArrivals.map((data) =>
          convertUnix(data, time_zone, "sv-SE")
        );
      tempZoneTSIShipment.expectedArrivalTime = tsiScip.expectedArrivalTime
        ? tsiScip.expectedArrivalTime
        : [undefined];
      tempZoneTSIShipment.expectedArrivalTimeSource =
        tsiScip.expectedArrivalTimeSource
          ? tsiScip.expectedArrivalTimeSource
          : [undefined];
      tempZoneTSIShipment.expectedArrivalTimeParsed =
        tempZoneTSIShipment.expectedArrivalTime
          ? tempZoneTSIShipment.expectedArrivalTime.map((arrival) =>
              arrival ? convertUnix(arrival, time_zone, "sv-SE") : undefined
            )
          : [undefined];
      tempZoneTSIShipment.arrivalDatetime = tsiScip.arrivalDatetime
        ? tsiScip.arrivalDatetime
        : [undefined];
      tempZoneTSIShipment.arrivalDatetimeSource = tsiScip.arrivalDatetimeSource
        ? tsiScip.arrivalDatetimeSource
        : [undefined];
      tempZoneTSIShipment.arrivalDatetimeParsed =
        tempZoneTSIShipment.arrivalDatetime
          ? tempZoneTSIShipment.arrivalDatetime.map((arrival) =>
              arrival ? convertUnix(arrival, time_zone, "sv-SE") : undefined
            )
          : [undefined];

      tempZoneTSIShipment.shipmentId = remainingVrids;
      tempZoneTSIShipment.scac = "";
      tempZoneTSIShipment.ocrVendor = "N";
      tempZoneTSIShipment.timeZone = time_zone;
      tempZoneTSIShipment.transferId = tsiScip.transferId;

      if (tempZoneTSIShipment.expectedUnits > 0) {
        // This is for debugging
        tempZoneTSIShipment.logicSource = "TSI Forecasts";
        scipForecastsSummary.push(tempZoneTSIShipment);
      }
    }
  }

  const scipUnscheduledVendors = scipData.filter(
    (scipItem) => scipItem.ib_flow === "DR"
  );

  for (const x of scipUnscheduledVendors) {
    scipForecastsSummary.push(x);
  }

  for (const x of tsiForecastsSCIP) {
    scipForecastsSummary.push(x);
  }

  const manifestedShipments = incomingIncomingShipments.filter(
    (shipment) => shipment.loadId !== "Not Yet Manifested"
  );

  // Aggregate incomingIncomingShipments by loadId
  const aggregatedByLoadId = new Map();
  manifestedShipments.forEach((shipment) => {
    if (!aggregatedByLoadId.has(shipment.loadId)) {
      // Create new entry with base data
      aggregatedByLoadId.set(shipment.loadId, {
        ...shipment,
        tempZone: "TOTAL",
        expectedUnits: shipment.expectedUnits || 0,
        expectedAsrsUnits: shipment.expectedAsrsUnits || 0,
        childExpectedUnits: shipment.childExpectedUnits || 0,
        childExpectedAsrsUnits: shipment.childExpectedAsrsUnits || 0,
        expectedArrivalTime: shipment.expectedArrivalTime || [],
        expectedArrivalTimeSource: shipment.expectedArrivalTimeSource || [],
        expectedArrivalTimeParsed: shipment.expectedArrivalTimeParsed || [],
        arrivalDatetime: shipment.arrivalDatetime || [],
        arrivalDatetimeSource: shipment.arrivalDatetimeSource || [],
        arrivalDatetimeParsed: shipment.arrivalDatetimeParsed || [],
        scheduledArrivals: shipment.scheduledArrivals || [],
        scheduledArrivalsParsed: shipment.scheduledArrivalsParsed || [],
        status: shipment.status || [],
      });
    } else {
      // Update existing entry
      const existing = aggregatedByLoadId.get(shipment.loadId);
      existing.expectedUnits += shipment.expectedUnits || 0;
      existing.expectedAsrsUnits += shipment.expectedAsrsUnits || 0;
      existing.childExpectedUnits += shipment.childExpectedUnits || 0;
      existing.childExpectedAsrsUnits += shipment.childExpectedAsrsUnits || 0;

      // Keep OCR vendor flag if any shipment has it
      if (shipment.ocrVendor === "Y") {
        existing.ocrVendor = "Y";
      }

      // Merge arrival time arrays
      if (shipment.expectedArrivalTime) {
        existing.expectedArrivalTime = [
          ...new Set([
            ...existing.expectedArrivalTime,
            ...shipment.expectedArrivalTime,
          ]),
        ];
        existing.expectedArrivalTimeSource = [
          ...new Set([
            ...existing.expectedArrivalTimeSource,
            ...shipment.expectedArrivalTimeSource,
          ]),
        ];
        existing.expectedArrivalTimeParsed = [
          ...new Set([
            ...existing.expectedArrivalTimeParsed,
            ...shipment.expectedArrivalTimeParsed,
          ]),
        ];
      }

      if (shipment.arrivalDatetime) {
        existing.arrivalDatetime = [
          ...new Set([
            ...existing.arrivalDatetime,
            ...shipment.arrivalDatetime,
          ]),
        ];
        existing.arrivalDatetimeSource = [
          ...new Set([
            ...existing.arrivalDatetimeSource,
            ...shipment.arrivalDatetimeSource,
          ]),
        ];
        existing.arrivalDatetimeParsed = [
          ...new Set([
            ...existing.arrivalDatetimeParsed,
            ...shipment.arrivalDatetimeParsed,
          ]),
        ];
      }

      if (shipment.scheduledArrivals) {
        existing.scheduledArrivals = [
          ...new Set([
            ...existing.scheduledArrivals,
            ...shipment.scheduledArrivals,
          ]),
        ];
        existing.scheduledArrivalsParsed = [
          ...new Set([
            ...existing.scheduledArrivalsParsed,
            ...shipment.scheduledArrivalsParsed,
          ]),
        ];
      }

      if (shipment.status) {
        existing.status = [
          ...new Set([...existing.status, ...shipment.status]),
        ];
      }
    }
  });

  // Convert aggregated map to array and sort
  incomingIncomingShipmentsLoadId = Array.from(
    aggregatedByLoadId.values()
  ).sort((a, b) => {
    const aMin =
      a.scheduledArrivals && a.scheduledArrivals.length > 0
        ? Math.min(...a.scheduledArrivals)
        : 0;
    const bMin =
      b.scheduledArrivals && b.scheduledArrivals.length > 0
        ? Math.min(...b.scheduledArrivals)
        : 0;
    return aMin - bMin;
  });

  // Sort incomingIncomingShipments
  incomingIncomingShipments = incomingIncomingShipments.sort((a, b) => {
    const aMin =
      a.scheduledArrivals && a.scheduledArrivals.length > 0
        ? Math.min(...a.scheduledArrivals)
        : 0;
    const bMin =
      b.scheduledArrivals && b.scheduledArrivals.length > 0
        ? Math.min(...b.scheduledArrivals)
        : 0;
    if (aMin > bMin) return 1;
    if (bMin > aMin) return -1;
    return 0;
  });

  // Sort incomingDockReceived
  incomingDockReceived = incomingDockReceived.sort((a, b) => {
    if (
      a.lastDockReceiveTime +
        (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000 >
      b.lastDockReceiveTime +
        (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000
    ) {
      return -1;
    }
    if (
      a.lastDockReceiveTime +
        (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000 <
      b.lastDockReceiveTime +
        (a.sla ? Math.min(a.sla, 24) : 24) * 60 * 60 * 1000
    ) {
      return 1;
    }
    return 0;
  });

  const { detailRows } = aggregateShipments(
    scipForecastsSummary,
    incomingDockReceived,
    incomingIncomingShipments,
    incomingUnscheduledShipments,
    time_zone
  );

  const manifestedVrids = countManifestedVrids(
    incomingIncomingShipments,
    incomingDockReceived,
    time_zone
  );

  // Create a Map to store aggregated data by loadId
  const aggregatedShipments = new Map();

  // Process each shipment
  incomingUnscheduledShipments.forEach((shipment) => {
    // Extract loadId from orderId (removing PO_ or Trans_ prefix)
    const loadId = (shipment.orderId = shipment.orderId
      .replace("PO_", "")
      .replace("Trans_", ""));

    if (!aggregatedShipments.has(loadId)) {
      // Create new entry with base shipment data
      aggregatedShipments.set(loadId, {
        ...shipment,
        loadId,
        tempZone: "TOTAL",
        expectedUnits: shipment.expectedUnits || 0,
        expectedAsrsUnits: shipment.expectedAsrsUnits || 0,
        childExpectedUnits: shipment.childExpectedUnits || 0,
        childExpectedAsrsUnits: shipment.childExpectedAsrsUnits || 0,
        ocrVendor: shipment.ocrVendor,
        expectedArrivalTime: shipment.expectedArrivalTime,
        expectedArrivalTimeSource: shipment.expectedArrivalTimeSource,
      });
    } else {
      // Update existing entry
      const existing = aggregatedShipments.get(loadId);
      existing.expectedUnits += shipment.expectedUnits || 0;
      existing.expectedAsrsUnits += shipment.expectedAsrsUnits || 0;
      existing.childExpectedUnits += shipment.childExpectedUnits || 0;
      existing.childExpectedAsrsUnits += shipment.childExpectedAsrsUnits || 0;

      // Keep OCR vendor flag if any shipment has it
      if (shipment.ocrVendor === "Y") {
        existing.ocrVendor = "Y";
      }

      // Update expected arrival time if this shipment has an earlier time
      if (shipment.expectedArrivalTime && shipment.expectedArrivalTime[0]) {
        if (
          !existing.expectedArrivalTime ||
          !existing.expectedArrivalTime[0] ||
          shipment.expectedArrivalTime[0] < existing.expectedArrivalTime[0]
        ) {
          existing.expectedArrivalTime = shipment.expectedArrivalTime;
          existing.expectedArrivalTimeSource =
            shipment.expectedArrivalTimeSource;
        }
      }
    }

    // Recalculate expectedAsrsUnitsPercent after updating units
    if (aggregatedShipments.has(loadId)) {
      const existing = aggregatedShipments.get(loadId);
      existing.expectedAsrsUnitsPercent = existing.expectedUnits
        ? Math.round(
            (existing.expectedAsrsUnits / existing.expectedUnits) * 100 * 100
          ) / 100
        : 0;
    }
  });

  // Convert Map to array
  const result = Array.from(aggregatedShipments.values());

  // Sort LoadId arrays by scheduled arrivals
  incomingIncomingShipmentsLoadId.sort((a, b) => {
    const aTime =
      a.scheduledArrivals && a.scheduledArrivals.length > 0
        ? Math.min(...a.scheduledArrivals)
        : 0;
    const bTime =
      b.scheduledArrivals && b.scheduledArrivals.length > 0
        ? Math.min(...b.scheduledArrivals)
        : 0;
    return aTime - bTime;
  });

  incomingUnscheduledShipmentsLoadId.sort((a, b) => {
    const aTime = Math.min(...(a.scheduledArrivals || []));
    const bTime = Math.min(...(b.scheduledArrivals || []));
    return aTime - bTime;
  });

  const notYetManifestedShipments = incomingIncomingShipments
    .filter((shipment) => shipment.loadId === "Not Yet Manifested")
    .reduce((acc, shipment) => {
      // Group by shipmentId array (since it can contain multiple IDs)
      const key = shipment.shipmentId.sort().join(",");
      if (!acc[key]) {
        // Create new entry with all original attributes
        acc[key] = {
          ...shipment,
          tempZone: "TOTAL",
          expectedUnits: shipment.expectedUnits,
        };
      } else {
        // Sum up expectedUnits for existing entry
        acc[key].expectedUnits += shipment.expectedUnits;
      }
      return acc;
    }, {});

  for (const x of Object.values(notYetManifestedShipments)) {
    incomingIncomingShipmentsLoadId.push(x);
  }

  return {
    parsedUnscheduledIncomingShipments: incomingUnscheduledShipments,
    parsedIncomingShipments: incomingIncomingShipments,
    parsedDockReceived: incomingDockReceived,
    detailRows: detailRows,
    manifestedVrids: manifestedVrids,
    parsedIncomingShipmentsLoadId: incomingIncomingShipmentsLoadId,
    parsedUnscheduledIncomingShipmentsLoadId: result,
  };
}

export { parseShipmentsUpdated };
